import { Button, Label } from '../ui'
import type { LatenessReportFilters } from '../../types/reports.types'

interface Organization {
  id: string
  name: string
}

interface Department {
  id: string
  name: string
  organization_id?: string | null
}

const MONTHS = [
  { value: 1, label: 'Январь' },
  { value: 2, label: 'Февраль' },
  { value: 3, label: 'Март' },
  { value: 4, label: 'Апрель' },
  { value: 5, label: 'Май' },
  { value: 6, label: 'Июнь' },
  { value: 7, label: 'Июль' },
  { value: 8, label: 'Август' },
  { value: 9, label: 'Сентябрь' },
  { value: 10, label: 'Октябрь' },
  { value: 11, label: 'Ноябрь' },
  { value: 12, label: 'Декабрь' },
]

const currentYear = new Date().getFullYear()
const YEARS = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i).reverse()

interface LatenessFiltersProps {
  filters: LatenessReportFilters
  onFiltersChange: (filters: Partial<LatenessReportFilters>) => void
  onGenerateReport: () => void
  onExport?: () => void
  canExport?: boolean
  organizations?: Organization[]
  departments?: Department[]
  isLoading?: boolean
}

export function LatenessFilters({
  filters,
  onFiltersChange,
  onGenerateReport,
  onExport,
  canExport = false,
  organizations = [],
  departments = [],
  isLoading = false,
}: LatenessFiltersProps) {
  const filteredDepartments = filters.organizationId
    ? departments.filter((d) => d.organization_id === filters.organizationId)
    : departments

  const handleClear = () => {
    const today = new Date()
    onFiltersChange({
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      organizationId: undefined,
      departmentId: undefined,
      onlyLate: false,
    })
  }

  const selectClassName =
    'h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        {/* Year */}
        <div className="space-y-2">
          <Label htmlFor="lateness-year">Год</Label>
          <select
            id="lateness-year"
            value={filters.year}
            onChange={(e) => onFiltersChange({ year: parseInt(e.target.value, 10) })}
            className={`${selectClassName} w-[120px]`}
          >
            {YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Month */}
        <div className="space-y-2">
          <Label htmlFor="lateness-month">Месяц</Label>
          <select
            id="lateness-month"
            value={filters.month}
            onChange={(e) => onFiltersChange({ month: parseInt(e.target.value, 10) })}
            className={`${selectClassName} w-[150px]`}
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Organization */}
        <div className="space-y-2">
          <Label htmlFor="lateness-organization">Организация</Label>
          <select
            id="lateness-organization"
            value={filters.organizationId || ''}
            onChange={(e) =>
              onFiltersChange({
                organizationId: e.target.value || undefined,
                departmentId: undefined,
              })
            }
            className={`${selectClassName} w-[220px]`}
          >
            <option value="">Все организации</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>

        {/* Department */}
        <div className="space-y-2">
          <Label htmlFor="lateness-department">Подразделение</Label>
          <select
            id="lateness-department"
            value={filters.departmentId || ''}
            onChange={(e) =>
              onFiltersChange({ departmentId: e.target.value || undefined })
            }
            className={`${selectClassName} w-[220px]`}
          >
            <option value="">Все подразделения</option>
            {filteredDepartments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>

        {/* Only late checkbox */}
        <div className="flex items-center gap-2 pb-2">
          <input
            type="checkbox"
            id="only-late"
            checked={filters.onlyLate || false}
            onChange={(e) => onFiltersChange({ onlyLate: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="only-late" className="cursor-pointer text-sm">
            Только опоздавшие
          </Label>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <Button onClick={onGenerateReport} disabled={isLoading}>
            {isLoading ? 'Загрузка...' : 'Сформировать отчет'}
          </Button>
          <Button
            variant="default"
            className="bg-green-600 hover:bg-green-700"
            disabled={!canExport}
            onClick={onExport}
          >
            <span className="mr-2">📊</span>
            Экспорт в Excel
          </Button>
          <Button variant="outline" onClick={handleClear}>
            Сбросить
          </Button>
        </div>
      </div>
    </div>
  )
}
